/**
 * State for the site-configuration backend.
 *
 * Owns the site-config store, the active locale, the current selection, and
 * the change history (see ./history.ts). Persistence is localStorage only.
 *
 * Site config has no `languages` collection of its own, so `locales` is
 * derived from the content itself — every locale any translation is written
 * under. It previously borrowed the Services backend's list, which meant a
 * locale translated here but not configured there never appeared in the
 * editor's dropdown and read as data loss.
 *
 * Scoped per team: dbId namespaces every key this hook touches. Passing a
 * different dbId (e.g. on a team switch) reloads the store and history from
 * that team's own keys.
 */

import { useStaging } from "../shared/useStaging"
import { useState, useEffect, useCallback, useRef } from "react"
import * as db from "./store"
import { toast } from "sonner"
import { type CmsAccess, checkSiteConfigWrite } from "../shared/permissions"
import { StoreSync, type SyncStatus } from "../shared/persistence/syncBridge"
import { makeSiteConfigAdapter } from "../shared/persistence/siteConfigAdapter"
import { containerId } from "../shared/persistence/types"
import { registerHistorySync, pullHistory } from "../shared/persistence/historySync"
import {
  type SiteConfigStore, type SiteConfigEntityKind, emptySiteConfigStore, siteConfigDisplayName,
  localesInSiteConfigStore, SITECONFIG_BUCKET,
} from "./types"
import { describeEdit } from "../shared/useRecordDraft"
import {
  type ChangeEntry, type ChangeAction, type EntityVersion,
  loadHistory, saveHistory, makeEntry, revertEntry, clearHistory,
  versionsForEntity, restoreEntityVersion,
} from "./history"

const LOCALE_KEY_PREFIX = "svc-siteconfig-locale"
const DEFAULT_DB = "default"

// The store's record buckets, which unpublished changes cover
const SITE_BUCKETS = Object.values(SITECONFIG_BUCKET) as string[]
const kindOfBucket = (bucket: string) =>
  (Object.keys(SITECONFIG_BUCKET) as (keyof typeof SITECONFIG_BUCKET)[]).find(k => SITECONFIG_BUCKET[k] === bucket)!

function localeKeyFor(dbId: string) { return dbId === DEFAULT_DB ? LOCALE_KEY_PREFIX : `${LOCALE_KEY_PREFIX}:${dbId}` }

/**
 * Which part of a SiteConfig the editor is showing.
 *
 * headerMenu and footer are embedded on the config rather than being their own
 * entities (see types.ts fidelity note 1b), so they cannot be a
 * SiteConfigEntityKind. They are still separately selectable in the tree, so
 * the selection carries a view discriminator instead.
 */
export type SiteConfigView = "settings" | "headerNav" | "footer"

export interface SiteConfigSelection {
  kind: SiteConfigEntityKind
  id: string
  /** Only meaningful for kind "site"; defaults to "settings". */
  view?: SiteConfigView
}

/** Identity recorded against each change, so the log shows who did what. */
export interface SiteConfigCMSAuthor {
  name?: string
  email?: string
  id?: string
  /** Model id, recorded when the agent is the one acting. */
  model?: string
}

/*
 * One sync bridge per hook instance, held outside React state: it owns a
 * debounce timer and an in-flight flag that must survive re-renders.
 */
export function useSiteConfigCMS(author?: SiteConfigCMSAuthor, dbId: string = DEFAULT_DB, access: CmsAccess = "manage") {
  const syncRef = useRef<StoreSync<SiteConfigStore>>(null as any)
  if (!syncRef.current) syncRef.current = new StoreSync<SiteConfigStore>()
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(() => ({
    state: "idle", lastSyncedAt: null, error: null, pending: false,
  }))
  // The team whose rows have loaded; until then the store is a placeholder
  const [loadedFor, setLoadedFor] = useState("")

  const [store, setStoreState] = useState<SiteConfigStore>(db.emptyStoreFor)
  const [locale, setLocale] = useState<string>(() => localStorage.getItem(localeKeyFor(dbId)) || store.defaultLocale)
  const [selected, setSelected] = useState<SiteConfigSelection | null>(null)
  const [history, setHistoryState] = useState<ChangeEntry[]>(() => loadHistory(dbId))

  // Mirror the store (and history/author) in refs so tool closures read
  // current values: an agent turn makes many sequential tool calls without
  // re-rendering, so a captured snapshot would go stale after the first
  // mutation.
  const storeRef = useRef(store)
  // Edits to existing records wait as unpublished changes; the live store is what the database and public sites have
  const staging = useStaging<SiteConfigStore>("site", SITE_BUCKETS, store)
  const historyRef = useRef(history)
  const authorRef = useRef(author)
  staging.setAuthor(() => ({ name: authorRef.current?.name, email: authorRef.current?.email }))
  const accessRef = useRef(access)
  accessRef.current = access
  useEffect(() => { authorRef.current = author }, [author])
  useEffect(() => { storeRef.current = store }, [store])
  useEffect(() => { historyRef.current = history }, [history])
  useEffect(() => { localStorage.setItem(localeKeyFor(dbId), locale) }, [dbId, locale])
  // dbId is read inside callbacks the same way as author — a ref avoids
  // re-creating every mutation callback whenever the team changes mid-render.
  const dbIdRef = useRef(dbId)

  // Team switch: reload the store and history from the new dbId's own keys
  // rather than continuing to show the previous team's data.
  useEffect(() => {
    if (dbIdRef.current === dbId) return
    dbIdRef.current = dbId
    // Cleared, not cached: the hydration effect below pulls this team's
    // real store from Postgres.
    const fresh = db.emptyStoreFor()
    staging.reset(fresh, dbId)
    storeRef.current = fresh
    setStoreState(fresh)
    const freshHistory = loadHistory(dbId)
    historyRef.current = freshHistory
    setHistoryState(freshHistory)
    setLocale(localStorage.getItem(localeKeyFor(dbId)) || fresh.defaultLocale)
    setSelected(null)
  }, [dbId])

  /*
   * Attach the database backend, if configured, and hydrate from it.
   *
   * The database is the only store. The UI renders empty until this resolves,
   * which is correct -- there is nothing local to show in the meantime.
   */
  useEffect(() => {
    const sync = syncRef.current
    // No team yet (still loading, or signed out) means there is nothing to
    // scope a query by -- not a fallback mode.
    if (!dbId) {
      sync.configure(null, "")
      registerHistorySync("site-config", null)
      return
    }
    let cancelledHistory = false
    // The change log is team-scoped, so it keys off the team id directly
    // rather than the derived container id the entity tables use.
    registerHistorySync("site-config", dbId)

    /*
     * Pull the change log too. Without this the log is write-only: entries
     * reach the database but every client reads its own localStorage copy,
     * so you cannot see -- or revert -- a change made from another browser.
     */
    void pullHistory("site-config", dbId).then(remote => {
      if (cancelledHistory || !remote) return
      historyRef.current = remote as ChangeEntry[]
      setHistoryState(remote as ChangeEntry[])
    })
    // The container row's PK is derived, not the bare team id -- see containerId.
    const container = containerId("site-config", dbId)
    sync.configure(makeSiteConfigAdapter(dbId), container)
    staging.reset(storeRef.current, dbId)

    let cancelled = false
    void (async () => {
      const result = await sync.pull()
      if (cancelled) return

      if (!result.ok) {
        /*
         * The load failed, so whatever is in memory came from the local cache
         * and may be stale. Block writes rather than letting a later edit
         * overwrite the database with it -- the indicator shows "Not saved"
         * and a retry re-runs this effect.
         */
        sync.blockWrites(`Could not load the $site configuration: ${result.error}`)
        return
      }

      // An empty team keeps what's in memory, which the first save seeds the database with
      const working = await staging.hydrate(result.store ?? storeRef.current)
      if (cancelled) return
      storeRef.current = working
      setStoreState(working)
      setLoadedFor(dbId)
      // result.empty means this team has no rows yet: keep whatever is in
      // memory, which the first save will seed the database with.
    })()
    return () => { cancelled = true; cancelledHistory = true }
  }, [dbId])

  useEffect(() => syncRef.current.subscribe(setSyncStatus), [])

  /*
   * A pending write must not be lost to a tab close. `flush` is fire-and-
   * forget here because beforeunload cannot await -- it improves the odds
   * without pretending to guarantee delivery, which is why the local copy
   * remains authoritative for recovery.
   */
  useEffect(() => {
    const onLeave = () => { void syncRef.current.flush() }
    window.addEventListener("beforeunload", onLeave)
    return () => {
      window.removeEventListener("beforeunload", onLeave)
      void syncRef.current.flush()
    }
  }, [])

  /** Single write path: React state, the ref, localStorage, and the DB. */
  const setStore = useCallback((next: SiteConfigStore) => {
    const access = accessRef.current
    const staged = staging.split(storeRef.current, next, access === "manage")
    // View-only can't change anything; otherwise only what goes live is checked against the role
    const denied = access === "view"
      ? checkSiteConfigWrite(storeRef.current, next, access)
      : checkSiteConfigWrite(staging.live(), staged.live, access)
    if (denied) {
      toast.error(denied, { id: "cms-denied" }) // one toast, not one per keystroke
      throw new Error(denied)
    }
    staging.commit(staged)
    storeRef.current = next
    setStoreState(next)
    // Only live changes go to the content tables; unpublished ones are saved by staging
    if (staged.liveChanged) syncRef.current.push(staged.live)
  }, [])


  const pushHistory = useCallback((entry: ChangeEntry) => {
    const next = [...historyRef.current, entry]
    historyRef.current = next
    setHistoryState(next)
    saveHistory(next, dbIdRef.current)
  }, [])

  /**
   * Records a change. Call with the store as it was BEFORE the mutation so the
   * snapshot is the pre-change state — that's what revert restores.
   */
  const record = useCallback((args: {
    prevStore: SiteConfigStore
    action: ChangeAction
    actor: "agent" | "user"
    entityKind: SiteConfigEntityKind
    entityId: string
    summary: string
    locale?: string
    /** Store after the change, for the `after` snapshot. Omit for deletes. */
    nextStore?: SiteConfigStore
  }) => {
    const { prevStore, nextStore, action, actor, entityKind, entityId, summary } = args

    const before = action === "create"
      ? undefined
      : JSON.parse(JSON.stringify(db.getEntity(prevStore, entityKind, entityId)))
    if (action !== "create" && !before) return // nothing to record against

    const afterEntity = nextStore ? db.getEntity(nextStore, entityKind, entityId) : null
    const identitySource = action === "create" ? (nextStore ?? prevStore) : prevStore
    const entityName = afterEntity
      ? siteConfigDisplayName(entityKind, afterEntity, args.locale ?? identitySource.defaultLocale, identitySource.defaultLocale)
      : before
        ? siteConfigDisplayName(entityKind, before, args.locale ?? identitySource.defaultLocale, identitySource.defaultLocale)
        : entityId

    const who = authorRef.current
    pushHistory(makeEntry({
      action, actor, entityKind, entityId, entityName,
      authorName: who?.name,
      authorEmail: who?.email,
      authorId: who?.id,
      viaModel: actor === "agent" ? who?.model : undefined,
      locale: args.locale,
      summary,
      before,
      after: afterEntity ? JSON.parse(JSON.stringify(afterEntity)) : undefined,
    }))
  }, [pushHistory])

  // ── UI mutations (actor: "user") ─────────────────────────────────────────

  // Managers publish: unpublished changes go live, each as one version
  const publishPending = useCallback((keys?: string[]) => {
    if (accessRef.current !== "manage") {
      toast.error("Only a manager can publish changes.", { id: "cms-denied" })
      return
    }
    // Only changes that are still waiting
    const chosen = (keys ?? staging.allKeys()).filter(k => staging.pendingEntity(k) !== undefined)
    if (!chosen.length) return
    const prevLive = staging.live()
    const plan = staging.publishPlan(chosen)
    syncRef.current.push(plan.live)
    staging.applyPlan(plan, chosen)
    storeRef.current = plan.working
    setStoreState(plan.working)
    for (const key of chosen) {
      const { bucket, id } = staging.splitKey(key)
      const before = (prevLive as any)[bucket]?.[id]
      const after = (plan.live as any)[bucket]?.[id]
      if (!before || !after) continue
      record({ prevStore: prevLive, nextStore: plan.live, action: "update", actor: "user", entityKind: kindOfBucket(bucket), entityId: id, summary: `Published: ${describeEdit(before, after).replace(/^Edited /, "")}` })
    }
  }, [record])

  // Throws unpublished changes away; the record goes back to what's live
  const discardPending = useCallback((keys: string[]) => {
    if (accessRef.current === "view" || !keys.length) return
    const prevWorking = storeRef.current
    const plan = staging.discardPlan(keys)
    staging.applyPlan(plan, keys)
    storeRef.current = plan.working
    setStoreState(plan.working)
    for (const key of keys) {
      const { id, bucket } = staging.splitKey(key)
      if (!(plan.working as any)[bucket]?.[id]) continue
      record({ prevStore: prevWorking, nextStore: plan.working, action: "update", actor: "user", entityKind: kindOfBucket(bucket), entityId: id, summary: "Discarded unpublished changes" })
    }
  }, [record])

  const create = useCallback((kind: SiteConfigEntityKind, fields: Record<string, any>) => {
    const prev = storeRef.current
    const { store: next, id } = db.createEntity(prev, kind, fields)
    setStore(next)
    record({
      prevStore: prev, nextStore: next, action: "create", actor: "user",
      entityKind: kind, entityId: id, locale,
      summary: `Created ${kind}`,
    })
    setSelected({ kind, id })
    return id
  }, [locale, setStore, record])

  // Reason the store refused the last write (see fieldWriteBlocker); shown in the editor instead of throwing from onChange
  const [writeError, setWriteError] = useState<string | null>(null)

  const update = useCallback((kind: SiteConfigEntityKind, id: string, fields: Record<string, any>) => {
    const prev = storeRef.current
    let next: SiteConfigStore
    try {
      next = db.updateEntity(prev, kind, id, fields)
    } catch (e: any) {
      setWriteError(e?.message || String(e))
      return
    }
    setWriteError(null)
    setStore(next)
    record({
      prevStore: prev, nextStore: next, action: "update", actor: "user",
      entityKind: kind, entityId: id, locale,
      summary: `Edited ${kind}`,
    })
  }, [setStore, record, locale])

  // An editor's Save: the record's draft replaces it as one change, one version
  const commitEntity = useCallback((kind: SiteConfigEntityKind, id: string, entity: any): boolean => {
    const prev = storeRef.current
    const before = db.getEntity(prev, kind, id)
    const bucket = SITECONFIG_BUCKET[kind]
    const next = { ...prev, [bucket]: { ...(prev as any)[bucket], [id]: { ...entity, updatedAt: Date.now() } } } as SiteConfigStore
    try { setStore(next) } catch { return false }
    record({
      prevStore: prev, nextStore: next, action: "update", actor: "user",
      entityKind: kind, entityId: id, locale,
      summary: describeEdit(before, entity),
    })
    return true
  }, [setStore, record, locale])

  const setTranslation = useCallback((kind: SiteConfigEntityKind, id: string, loc: string, patch: Record<string, any>) => {
    const prev = storeRef.current
    let next: SiteConfigStore
    try {
      next = db.setTranslation(prev, kind, id, loc, patch)
    } catch (e: any) {
      setWriteError(e?.message || String(e))
      return
    }
    setWriteError(null)
    setStore(next)
    record({
      prevStore: prev, nextStore: next, action: "translate", actor: "user",
      entityKind: kind, entityId: id, locale: loc,
      summary: `Translated ${kind} (${loc})`,
    })
  }, [setStore, record])

  // Applies a batch of translations for one locale (used by the locale copy), one history entry per record
  const applyTranslations = useCallback((
    writes: Array<{ kind: SiteConfigEntityKind; id: string; translation: Record<string, any> }>,
    loc: string,
    summary: string,
  ): { applied: number } => {
    const prev = storeRef.current
    const { store: next, applied } = db.setTranslationsBulk(
      prev,
      writes.map(w => ({ kind: w.kind, id: w.id, locale: loc, translation: w.translation })),
    )
    if (!applied) return { applied: 0 }

    setStore(next)
    for (const w of writes) {
      record({
        prevStore: prev, nextStore: next, action: "translate", actor: "user",
        entityKind: w.kind, entityId: w.id, locale: loc, summary,
      })
    }
    return { applied }
  }, [setStore, record])

  const remove = useCallback((kind: SiteConfigEntityKind, id: string) => {
    const prev = storeRef.current
    const label = db.displayName(prev, kind, id, locale)
    // Record before applying so the snapshot captures the still-intact entity.
    record({
      prevStore: prev, action: "delete", actor: "user",
      entityKind: kind, entityId: id, locale,
      summary: `Deleted ${kind} "${label}"`,
    })
    const { store: next } = db.deleteEntity(prev, kind, id)
    setStore(next)
    // A delete can take the current selection with it via detachment even
    // when it wasn't the direct target. Check the post-delete store rather
    // than just comparing ids to what was deleted.
    setSelected(cur => (cur && !db.getEntity(next, cur.kind, cur.id) ? null : cur))
  }, [locale, setStore, record])

  const reset = useCallback(() => {
    setStore(emptySiteConfigStore(storeRef.current.defaultLocale || "en-US"))
    setSelected(null)
  }, [setStore])

  // ── History ──────────────────────────────────────────────────────────────

  const revert = useCallback((entryId: string): { ok: true; description: string } | { ok: false; error: string } => {
    const entry = historyRef.current.find(e => e.id === entryId)
    if (!entry) return { ok: false, error: `No history entry with id ${entryId}` }

    const result = revertEntry(storeRef.current, entry)
    if ("error" in result) return { ok: false, error: result.error }

    setStore(result.store)

    // Mark the original reverted, and log the revert itself so the trail is
    // complete — without this second entry the log shows a change greyed out
    // with no record of who undid it or when.
    const who = authorRef.current
    const marked = historyRef.current.map(e => e.id === entryId ? { ...e, reverted: true } : e)
    const withRevert = [...marked, makeEntry({
      action: "revert",
      actor: "user",
      authorName: who?.name,
      authorEmail: who?.email,
      authorId: who?.id,
      entityKind: entry.entityKind,
      entityId: entry.entityId,
      entityName: entry.entityName,
      locale: entry.locale,
      summary: result.description,
    })]
    historyRef.current = withRevert
    setHistoryState(withRevert)
    saveHistory(withRevert, dbIdRef.current)

    return { ok: true, description: result.description }
  }, [setStore])

  /** Version timeline for one entity, newest first. */
  const versionsOf = useCallback((entityId: string): EntityVersion[] =>
    versionsForEntity(historyRef.current, entityId), [])

  /**
   * Restores an entity to one of its recorded versions. Logged as an update,
   * so the restore is itself revertable.
   */
  const restoreEntityVersionAndRecord = useCallback((entityId: string, entryId: string, kind: SiteConfigEntityKind):
    { ok: true; description: string } | { ok: false; error: string } => {
    const version = versionsForEntity(historyRef.current, entityId)
      .find(v => v.entryId === entryId && !!v.entity)
    if (!version?.entity) return { ok: false, error: "That version has no saved snapshot to restore." }

    const prev = storeRef.current
    const next = restoreEntityVersion(prev, kind, version.entity)
    setStore(next)
    record({
      prevStore: prev, nextStore: next, action: "update", actor: "user",
      entityKind: kind, entityId, locale,
      summary: `Restored to version from ${new Date(version.at).toLocaleString()} (${version.author})`,
    })
    return { ok: true, description: `Restored to the ${new Date(version.at).toLocaleString()} version` }
  }, [setStore, record, locale])

  const wipeHistory = useCallback(() => {
    if (accessRef.current !== "manage") {
      toast.error("Only a manager can clear the change history.")
      return
    }
    clearHistory(dbIdRef.current)
    historyRef.current = []
    setHistoryState([])
  }, [])

  // ── Derived reads ────────────────────────────────────────────────────────

  const nameOf = useCallback((kind: SiteConfigEntityKind, id: string): string => {
    return db.displayName(storeRef.current, kind, id, locale)
  }, [locale])

  return {
    store, setStore,
    /** Always-current store reader for tool closures. See storeRef above. */
    getStore: () => storeRef.current,
    locale, setLocale,
    /** Database sync state; "offline" means no team selected yet. */
    syncStatus,
    /** Unpublished changes: what's waiting, and the store as it's live. */
    pending: {
      available: staging.available,
      keys: staging.pendingKeys,
      // Includes changes React hasn't rendered yet
      allKeys: staging.allKeys,
      liveStore: staging.liveStore,
      keyOf: staging.keyOf,
      liveEntity: staging.liveEntity,
      canPublish: access === "manage",
      publish: publishPending,
      discard: discardPending,
    },
    /** True once this team's rows have loaded from the database. */
    loaded: !!dbId && loadedFor === dbId,
    /** Forces a pending write out immediately. */
    flushSync: () => syncRef.current.flush(),
    /** Locales with content, default first. Derived — see localesInSiteConfigStore. */
    locales: localesInSiteConfigStore(store),
    selected, setSelected,
    create, update, setTranslation, commitEntity, remove, reset,
    applyTranslations,
    writeError,
    dismissWriteError: () => setWriteError(null),
    nameOf,
    stats: db.stats(store),

    history,
    /** Always-current history reader for tool closures. */
    getHistory: () => historyRef.current,
    record,
    revert,
    versionsOf,
    restoreEntityVersion: restoreEntityVersionAndRecord,
    wipeHistory,
  }
}
